import React, { useCallback, useMemo, useState } from 'react'

import { faPencil, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { Flex } from 'antd'
import { Modal, RadioGroup } from 'govwell-ui'
import { AIChatFallbackContactFragment, UserDataFragment } from 'types/graphql'

import {
  useCreateAIChatFallbackContactMutation,
  useUpdateAIChatFallbackContactMutation,
} from 'src/fetch/aiChatFallbackContacts'
import { useAppForm } from 'src/hooks/use-app-form'
import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { RefetchFnType } from 'src/types'

type FormValues = {
  fallbackConditions: string
  contactString: string
  user: UserDataFragment | null | undefined
}

type Props = {
  aiChatFallbackContact?: AIChatFallbackContactFragment
  onClose: () => void
  refetch: RefetchFnType
}

type ModalType = 'edit' | 'create'
type ContactMode = 'govwell-user' | 'outside-user'

const AIChatFallbackContactModal = ({ aiChatFallbackContact, onClose, refetch }: Props) => {
  const { mutateAsync: updateAIChatFallbackContact } = useUpdateAIChatFallbackContactMutation()
  const { mutateAsync: createAIChatFallbackContact } = useCreateAIChatFallbackContactMutation()
  const organizationId = useMyOrgId()
  const modalType: ModalType = aiChatFallbackContact ? 'edit' : 'create'

  const [contactMode, setContactMode] = useState<ContactMode>(() => {
    if (aiChatFallbackContact?.user) {
      return 'govwell-user'
    }
    return 'outside-user'
  })

  const initialValues: FormValues = useMemo(
    () => ({
      fallbackConditions: aiChatFallbackContact?.fallbackConditions ?? '',
      contactString: aiChatFallbackContact?.contactString ?? '',
      user: aiChatFallbackContact?.user ?? null,
    }),
    [aiChatFallbackContact]
  )

  const form = useAppForm({
    defaultValues: initialValues,
  })

  const handleSubmit = useCallback(async () => {
    // for both fallbackConditions and contactString:
    // if the form value is an empty string, set the db value to null
    const fallbackConditions = form.getFieldValue('fallbackConditions') || null
    const contactString = form.getFieldValue('contactString') || null
    const userId = contactMode === 'govwell-user' ? (form.getFieldValue('user')?.id ?? null) : null

    if (modalType === 'edit' && aiChatFallbackContact?.id) {
      await updateAIChatFallbackContact({
        id: aiChatFallbackContact.id,
        input: {
          fallbackConditions,
          contactString,
          userId,
        },
      })
    } else if (modalType === 'create') {
      await createAIChatFallbackContact({
        input: {
          fallbackConditions,
          contactString,
          userId,
          organizationId,
        },
      })
    }
    await refetch()
  }, [
    aiChatFallbackContact?.id,
    contactMode,
    createAIChatFallbackContact,
    form,
    modalType,
    organizationId,
    refetch,
    updateAIChatFallbackContact,
  ])

  const modalTitle = useMemo(() => {
    if (modalType === 'edit') {
      return 'Update Fallback Contact'
    }
    return 'Create Fallback Contact'
  }, [modalType])

  const modalIcon = useMemo(() => {
    if (modalType === 'edit') {
      return faPencil
    }
    return faPlus
  }, [modalType])

  const modalConfirmText = useMemo(() => {
    if (modalType === 'edit') {
      return 'Update Contact'
    }
    return 'Create Contact'
  }, [modalType])

  return (
    <Modal onClose={onClose} width="50%">
      <Modal.Header icon={modalIcon}>{modalTitle}</Modal.Header>
      <Modal.Content>
        <Flex vertical gap="24px">
          <form.AppField name="fallbackConditions">
            {(field) => (
              <field.TextArea
                caption="Specify the conditions when this fallback contact should be used."
                field={field}
                label="Fallback Conditions"
                minRows={3}
                placeholder="e.g., when user asks about permits, when query contains specific keywords"
                isRequired
              />
            )}
          </form.AppField>
          <RadioGroup
            ariaLabel="Contact Mode"
            variant="tiles"
            onValueChange={setContactMode}
            value={contactMode}
          >
            <RadioGroup.Item
              value="govwell-user"
              icon="FaUser"
              label="GovWell User"
              description="Select from existing users"
            />
            <RadioGroup.Item
              value="outside-user"
              icon="FaPencil"
              label="Someone outside of GovWell"
              description="Add external contact info"
            />
          </RadioGroup>

          {contactMode === 'govwell-user' && (
            <Flex vertical gap="16px">
              <form.AppField name="user">
                {(field) => (
                  <field.StaffUserSelect
                    field={field}
                    isMultiSelect={false}
                    label="User"
                    isRequired
                    type="AnyUserInOrganization"
                  />
                )}
              </form.AppField>
              <form.AppField name="contactString">
                {(field) => (
                  <field.TextArea
                    field={field}
                    label="Contact Details"
                    placeholder="Add any additional details or instructions for this contact..."
                  />
                )}
              </form.AppField>
            </Flex>
          )}

          {contactMode === 'outside-user' && (
            <form.AppField name="contactString">
              {(field) => (
                <field.TextArea
                  field={field}
                  isRequired
                  label="Contact Information"
                  placeholder="Describe who and how to contact (e.g., 'Email support@gotham-ny.gov or call (555) 123-4567')"
                />
              )}
            </form.AppField>
          )}
        </Flex>
      </Modal.Content>
      <Modal.Footer confirmText={modalConfirmText} onConfirm={handleSubmit} />
    </Modal>
  )
}

export default React.memo(AIChatFallbackContactModal)
